import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { orderItemRepository, orderRepository } from '../../db/repositories';
import { db } from '../../db/database';
import type { OrderItemRecord, OrderRecord, OutboxStatus } from '../../db/types';
import { PricingEngine } from '../../domain/pricing';
import { getReceiptAddOnLines } from '../../domain/receipts/receiptUtils';
import { AddOnSelectionList } from './AddOnSelectionList';

type Props = { refreshKey: number; onClose: () => void };

function syncState(statuses: OutboxStatus[]) {
  if (statuses.includes('FAILED')) return 'FAILED';
  if (statuses.includes('SYNCING')) return 'SYNCING';
  if (statuses.includes('PENDING')) return 'PENDING';
  if (statuses.includes('SYNCED')) return 'SYNCED';
  return 'NO OUTBOX EVENT';
}

export function LocalOrdersDialog({ refreshKey, onClose }: Props) {
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [items, setItems] = useState<Record<string, OrderItemRecord[]>>({});
  const [syncStates, setSyncStates] = useState<Record<string, string>>({});
  useEffect(() => {
    void Promise.all([orderRepository.list(), db.outbox.toArray()]).then(async ([list, events]) => {
      setOrders(list);
      const pairs = await Promise.all(
        list.map(async (o) => [o.id, await orderItemRepository.listByOrderId(o.id)] as const),
      );
      setItems(Object.fromEntries(pairs));
      const states = Object.fromEntries(
        list.map((order) => [
          order.id,
          syncState(
            events.filter((event) => event.aggregateId === order.id).map((event) => event.status),
          ),
        ]),
      );
      setSyncStates(states);
    });
  }, [refreshKey]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-5">
      <div className="flex max-h-[90dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Local database
            </p>
            <h2 className="text-xl font-black text-[#003B70]">Saved orders on this iPad</h2>
          </div>
          <button
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100"
          >
            <X />
          </button>
        </div>
        <div className="min-h-0 overflow-auto p-5">
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="p-10 text-center font-semibold text-slate-400">
                No completed orders saved locally yet.
              </div>
            ) : (
              orders.map((order) => (
                <div key={order.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-black text-[#003B70]">{order.orderNumber}</p>
                      <p className="text-xs font-semibold text-slate-400">
                        Claim code:{' '}
                        <span className="font-black text-slate-700">{order.claimCode}</span> •{' '}
                        {new Date(order.createdAt).toLocaleString('en-PH')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-[#003B70]">
                        {PricingEngine.formatCentavos(order.totalCentavos)}
                      </p>
                      <p className="text-xs font-black text-emerald-600">{order.paymentStatus}</p>
                      <p
                        className={`mt-1 text-xs font-black ${syncStates[order.id] === 'FAILED' ? 'text-red-700' : syncStates[order.id] === 'SYNCED' ? 'text-emerald-700' : 'text-amber-700'}`}
                      >
                        Sync: {syncStates[order.id] ?? 'CHECKING'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(items[order.id] ?? []).map((item) => (
                      <span
                        key={item.id}
                        className="rounded-lg bg-[#F7FAFC] px-3 py-2 text-xs font-bold text-slate-600"
                      >
                        {item.serviceItemName
                          ? `${item.serviceItemName} × ${item.quantity}`
                          : `${item.serviceName} × ${item.quantity}${item.weightKg ? ` • ${item.weightKg} KG` : ''}`}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3">
                    <AddOnSelectionList
                      items={getReceiptAddOnLines(items[order.id] ?? [])}
                      label={`${order.orderNumber} add-ons`}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
